import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/shared";
import { Building2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ParksPage() {
  const { refresh } = usePark();
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", city: "", state: "" });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/parks"); setParks(data); }
    catch { toast.error("Failed to load parks"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm({ name: "", city: "", state: "" }); setOpen(true); };
  const openEdit = (p) => { setEditingId(p.id); setForm({ name: p.name, city: p.city || "", state: p.state || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Park name is required"); return; }
    try {
      if (editingId) {
        const existing = parks.find((p) => p.id === editingId);
        await api.put(`/parks/${editingId}`, { ...existing, ...form });
        toast.success("Park updated");
      } else {
        await api.post("/parks", form);
        toast.success("Park created");
      }
      setOpen(false);
      await load();
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete park "${p.name}"? All tenants, lots, reservations and invoices for this park will be wiped.`)) return;
    try {
      await api.delete(`/parks/${p.id}`);
      toast.success("Park deleted");
      await load();
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="parks-page">
      <PageHeader
        title="Parks"
        subtitle={`${parks.length} park${parks.length === 1 ? "" : "s"} in your portfolio`}
        actions={<Button onClick={openNew} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="new-park-button"><Plus size={14} className="mr-2" /> New Park</Button>}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!bg-white border-[#EAEAE5]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{editingId ? "Edit Park" : "New Park"}</DialogTitle>
            <DialogDescription className="text-xs text-[#737A76]">Each park has its own tenants, lots, reservations and FreedomCRM subaccount.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Park name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="park-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            </div>
            <p className="text-xs text-[#737A76]">Detailed settings (rates, taxes, FreedomCRM) are managed in <strong>Park Setup</strong> after switching to this park.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={save} data-testid="save-park-submit">{editingId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm pd-table">
            <thead><tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
              <th className="px-4 py-3">Park</th><th>Location</th><th>FreedomCRM</th><th></th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} className="px-4 py-8 text-center text-[#737A76]">Loading…</td></tr>
              : parks.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-[#737A76]">No parks yet. Create your first.</td></tr>
              : parks.map((p) => (
                <tr key={p.id} className="border-b border-[#F0F0EC] last:border-0" data-testid={`park-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium flex items-center gap-2"><Building2 size={14} className="text-[#737A76]" /> {p.name}</td>
                  <td className="text-[#737A76]">{[p.city, p.state].filter(Boolean).join(", ") || "—"}</td>
                  <td>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.ghl_enabled ? "bg-[#E3F0E6] text-[#297343]" : "bg-[#F0F0EC] text-[#737A76]"}`}>
                      {p.ghl_enabled ? "Connected" : "Not connected"}
                    </span>
                  </td>
                  <td className="text-right pr-4">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)}><Trash2 size={14} className="text-[#A13E34]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
